/**
 * Fetches Sanity content via the server-side BFF proxy (`/api/sanity-secure`).
 *
 * Drafts-vs-published is decided entirely server-side: the BFF authorises against the
 * httpOnly `sanity-preview-id` cookie and then reads the Presentation tool's perspective
 * from the `sanity-preview-perspective` cookie, which @nuxtjs/sanity keeps in sync over
 * comlink. Nothing about the perspective travels in the request body — a client-supplied
 * perspective would be an unauthenticated route to draft content.
 *
 * Live preview updates arrive via the visual-editing overlay's `refresh` handler.
 */
import { useFetch, useRoute } from 'nuxt/app';
import type { AsyncData } from 'nuxt/app';
import { toRaw } from 'vue';
import { getUserLanguage } from '@pkg/ui/composables/useUserLanguage';
import { updateUrlLocale } from './updateUrlLocale';

function sanitySecureFetchKey(query: string, params?: Record<string, unknown>) {
  return `sanity-secure:${query}:${JSON.stringify(params ?? {})}`;
}

function fetchSanityViaBff<TResult>(query: string, params?: Record<string, unknown>) {
  return useFetch<TResult>('/api/sanity-secure', {
    method: 'POST',
    body: {
      query,
      params,
    },
    key: sanitySecureFetchKey(query, params),
  } as unknown as Parameters<typeof useFetch<TResult>>[1]);
}

export function useSanityData<TResult = unknown, Err = Error>(
  query: string,
  params?: Record<string, any>,
): Promise<AsyncData<TResult | null, Err>> {
  const fetchSanityData = async() => {
    const responsePromise = await fetchSanityViaBff<TResult>(query, params);
    const data = (toRaw(responsePromise?.data?.value) as any) ?? {};

    if (data && data.page) {
      if (typeof data.page.slug === 'string' && data.page.parent != null) {
        const pathParts = data.page.slug?.split('/').filter(Boolean);
        const allUrlPartsPresent = pathParts.every((item: string) => Object.values(params || '').includes(item));
        if (!allUrlPartsPresent) {
          data.page = null;
          (responsePromise.data as { value: TResult | null }).value = data as TResult | null;
          return responsePromise as AsyncData<TResult | null, Err>;
        }
      }
    }

    const shouldRedirect = data?.page?.browserLocaleRedirect;
    if (shouldRedirect) {
      const userLanguage = getUserLanguage() || 'en-gb';
      const newParams = { ...(params || {}), locale: userLanguage };
      const redirectedResponse = await fetchSanityViaBff<TResult>(query, newParams);
      const currentRoute = useRoute();
      updateUrlLocale(userLanguage, currentRoute.path);
      return redirectedResponse;
    }

    return responsePromise;
  };

  return fetchSanityData() as Promise<AsyncData<TResult | null, Err>>;
}
